import ChannelIcon from "components/shared/ChannelIcon";
import LargeSwitch from "components/shared/LargeSwitch";
import { CalculationMethod } from "containers/Channels";
import type { IChannel } from "containers/Channels/types";
import type { ChannelData, ChannelParams, ITermFormData } from "containers/TermFormContainer";
import { showsDefaultChannelConfiguration } from "containers/TermFormContainer/utils";
import { useTranslation } from "react-i18next";
import type { CalculationBaseColumn, ICalculationBaseColumnLabel } from "store/calculationBaseColumnLabelsStore";

import variables from "../../../../assets/styles/_variables.module.scss";
import ChannelBaseColumnsFields, { channelUsesSalesBaseColumns } from "../ChannelBaseColumnsFields";
import { resolveChannelMilestoneBaseColumn } from "../milestoneBaseColumnUtils";
import MilestonesInput from "../MilestonesInput";
import ShowDefaultConfigurationField from "../ShowDefaultConfigurationField";
import ChannelFormatOverrides from "./ChannelFormatOverrides";

interface IChannelPercentages {
    /**
     * Channel that is displayed.
     */
    channel: IChannel;
    /**
     * Is the channel currently active in the form?
     */
    currentlyActive: boolean;
    /**
     * Is the channel active on the title? (on another contract)
     */
    activeOnTitle: boolean;
    /**
     * Handles activating channel and setting initial data.
     */
    handleChannelActivated: (
        e: React.ChangeEvent<HTMLInputElement>,
        channelId: number,
        calculationMethod: number,
    ) => void;
    /**
     * Saves any changes made to percentages/tiers
     */
    handleChannelUpdates: (newChannel: any, groupId?: number) => void;
    handleToggleFormatTypeOverride: (channelId: number, formatTypeId: number, enabled: boolean) => void;
    handleCalculationBaseColumnChange: (
        channelId: number,
        calculationBaseColumn: CalculationBaseColumn,
        formatTypeId?: number | null,
    ) => void;
    handleMilestoneColumnChange: (
        channelId: number,
        milestoneBaseColumn: CalculationBaseColumn,
        formatTypeId?: number | null,
    ) => void;
    calculationBaseColumnLabels: ICalculationBaseColumnLabel[];
    /**
     * Are we accessing form from "new" mode or "edit"
     */
    mode: "edit" | "new";
    /**
     * Data from same channel saved in the chosenChannels state.
     */
    existingChannel: ChannelParams | undefined;
    /**
     * Whether they have used template to complete form.
     */
    usesTemplate: boolean | undefined;
    useFormData: Pick<ITermFormData, "setError" | "clearErrors">;
    chosenChannels: ChannelParams[];
    formatTypesOnWork?: { id: number; name: string }[];
    /** Typology modal can hide the default formula on the contract form. */
    from?: "templates" | "form";
    onShowDefaultConfigurationChange?: (channelId: number, showDefaultConfiguration: boolean) => void;
}

// Section of contract that displays channels: if not active, only name with switch, if active also displays inputs for percentages/tiers. Specific to channels not in groups.
const ChannelPercentages = ({
    channel,
    currentlyActive,
    activeOnTitle,
    handleChannelActivated,
    handleChannelUpdates,
    handleToggleFormatTypeOverride,
    handleCalculationBaseColumnChange,
    handleMilestoneColumnChange,
    calculationBaseColumnLabels,
    mode,
    existingChannel,
    usesTemplate,
    useFormData,
    chosenChannels,
    formatTypesOnWork = [],
    from = "form",
    onShowDefaultConfigurationChange,
}: IChannelPercentages) => {
    const [t] = useTranslation();
    const { setError, clearErrors } = useFormData;
    const showDefaultConfiguration = showsDefaultChannelConfiguration(existingChannel);
    const isProportional = channel.calculationMethod === CalculationMethod.PROPORTIONAL;
    const showDefaultToggle = from === "templates" && isProportional;
    const hasFormatOverride = chosenChannels.some((row) => row.channelId === channel.id && row.formatTypeId != null);
    const canOfferFormats = isProportional && (formatTypesOnWork.length > 0 || hasFormatOverride);

    return (
        <div className="channel-line" key={`channel-${channel.id}`}>
            <div className="channel-top">
                <div className="channel-title">
                    <div style={{ display: "flex", minHeight: "20px", minWidth: "20px" }}>
                        <ChannelIcon
                            channelKind={channel.kind}
                            color={`${currentlyActive ? "#141516" : variables["foreground"]}`}
                        />
                    </div>
                    <span
                        style={{
                            color: `${currentlyActive ? "#141516" : variables["foreground"]}`,
                        }}
                        id={`channel-${channel.id}-title`}
                    >
                        {channel.name}
                    </span>
                </div>
                <div className="switch" id={`switch-${channel.id}`}>
                    {activeOnTitle && !currentlyActive && (
                        <div className="channel-reminder ">
                            <div className="chip" />
                            {t("ConnectTitle.ActiveChannel")}
                        </div>
                    )}
                    {currentlyActive && channel.calculationMethod === CalculationMethod.PACKAGE && (
                        <div className="channel-reminder channel-activated">
                            <div className="chip channel-activated" />
                            {t("ConnectTitle.ChannelActivated")}
                        </div>
                    )}
                    {
                        <LargeSwitch
                            onChange={(e: any) => handleChannelActivated(e, channel.id, channel.calculationMethod)}
                            inputProps={{ "aria-label": "controlled" }}
                            checked={currentlyActive}
                            className={currentlyActive && usesTemplate ? "template" : ""}
                        />
                    }
                </div>
            </div>
            {currentlyActive &&
                channelUsesSalesBaseColumns(channel) &&
                (showDefaultConfiguration || showDefaultToggle || canOfferFormats) && (
                    <div className="channel-config">
                        {showDefaultToggle && (
                            <ShowDefaultConfigurationField
                                checked={showDefaultConfiguration}
                                onChange={(showDefaultConfiguration) =>
                                    onShowDefaultConfigurationChange?.(channel.id, showDefaultConfiguration)
                                }
                            />
                        )}
                        {showDefaultConfiguration && (
                            <>
                                <ChannelBaseColumnsFields
                                    channel={channel}
                                    existingChannel={existingChannel}
                                    calculationBaseColumnLabels={calculationBaseColumnLabels}
                                    onCalculationBaseColumnChange={handleCalculationBaseColumnChange}
                                    onMilestoneColumnChange={handleMilestoneColumnChange}
                                />
                                {(channel.calculationMethod === CalculationMethod.PROPORTIONAL ||
                                    channel.calculationMethod === CalculationMethod.SUBSCRIPTION) && (
                                    <div className="channel-milestones">
                                        <MilestonesInput
                                            hasMilestones={!!channel.hasMilestones}
                                            channel={channel}
                                            milestoneBaseColumn={resolveChannelMilestoneBaseColumn(
                                                channel,
                                                existingChannel,
                                            )}
                                            onChange={(newChannel: any) =>
                                                handleChannelUpdates({ ...newChannel, formatTypeId: null })
                                            }
                                            propMilestones={existingChannel?.data as ChannelData[]}
                                            mode={mode}
                                            useFormData={{ setError, clearErrors }}
                                        />
                                    </div>
                                )}
                            </>
                        )}
                        <ChannelFormatOverrides
                            channel={channel}
                            formatTypesOnWork={formatTypesOnWork}
                            chosenChannels={chosenChannels}
                            calculationBaseColumnLabels={calculationBaseColumnLabels}
                            mode={mode}
                            useFormData={{ setError, clearErrors }}
                            handleToggleFormatTypeOverride={handleToggleFormatTypeOverride}
                            handleChannelUpdates={handleChannelUpdates}
                            handleCalculationBaseColumnChange={handleCalculationBaseColumnChange}
                            handleMilestoneColumnChange={handleMilestoneColumnChange}
                        />
                    </div>
                )}
        </div>
    );
};

export default ChannelPercentages;
